import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  UserProfile,
  UserRole,
  Announcement,
  Parcel,
  MaintenanceTicket,
  CommonFeeBill,
  VisitorPass,
  Facility,
  FacilityBooking,
  AttendanceRecord,
  PatrolCheckpoint,
  PatrolScanLog,
  ClockInStatus,
  ClockOutStatus,
  PatrolStatus,
  ResidentDirectoryItem,
  StaffLeaveRequest,
  StaffLeaveQuota,
  LeaveStatus,
  BoardDiscussion,
  BoardVote,
  BoardComment,
  FinancialMonthMetric,
} from '../types';
import { getSupabaseClient, getStoredConfig } from '../lib/supabase';
import { INITIAL_RESIDENTS } from '../data/mockResidents';
import { INITIAL_LEAVE_QUOTAS, INITIAL_LEAVE_REQUESTS } from '../data/mockStaffLeave';
import { INITIAL_BOARD_DISCUSSIONS, INITIAL_FINANCIAL_METRICS } from '../data/mockBoardAndFinance';

interface AppContextType {
  currentUser: UserProfile | null;
  authReady: boolean;
  selectedRole: UserRole;
  setSelectedRole: (role: UserRole) => void;
  login: (profile: UserProfile) => void;
  logout: () => void;
  
  // Data
  announcements: Announcement[];
  parcels: Parcel[];
  maintenanceTickets: MaintenanceTicket[];
  bills: CommonFeeBill[];
  visitorPasses: VisitorPass[];
  facilities: Facility[];
  bookings: FacilityBooking[];
  attendanceRecords: AttendanceRecord[];
  patrolCheckpoints: PatrolCheckpoint[];
  patrolLogs: PatrolScanLog[];
  residents: ResidentDirectoryItem[];
  leaveRequests: StaffLeaveRequest[];
  leaveQuotas: StaffLeaveQuota[];
  boardDiscussions: BoardDiscussion[];
  financialMetrics: FinancialMonthMetric[];
  
  // Actions
  addAnnouncement: (announcement: Omit<Announcement, 'id' | 'created_at'>) => Promise<void>;
  deleteAnnouncement: (id: string) => Promise<void>;
  addParcel: (parcel: Omit<Parcel, 'id' | 'received_at' | 'status'>) => Promise<void>;
  markParcelPickedUp: (id: string) => Promise<void>;
  createMaintenanceTicket: (ticket: Omit<MaintenanceTicket, 'id' | 'created_at' | 'updated_at' | 'status'>) => Promise<void>;
  updateMaintenanceStatus: (id: string, status: MaintenanceTicket['status'], staffNotes?: string, assignedTo?: string) => Promise<void>;
  payBill: (id: string, slipUrl: string) => Promise<void>;
  verifyBill: (id: string, status: 'paid' | 'overdue', verifiedBy: string) => Promise<CommonFeeBill | null>;
  rejectBill: (id: string, reason: string, verifiedBy: string) => Promise<void>;
  createVisitorPass: (pass: Omit<VisitorPass, 'id' | 'created_at' | 'status' | 'pass_code'>) => Promise<VisitorPass>;
  checkInVisitor: (passCode: string) => Promise<boolean>;
  checkOutVisitor: (passCode: string) => Promise<boolean>;
  bookFacility: (booking: Omit<FacilityBooking, 'id' | 'created_at' | 'status'>) => Promise<boolean>;
  cancelBooking: (id: string) => Promise<void>;
  
  // Resident Directory Actions
  addResident: (resident: Omit<ResidentDirectoryItem, 'id'>) => Promise<void>;
  updateResident: (id: string, data: Partial<ResidentDirectoryItem>) => Promise<void>;

  // Staff Attendance Actions
  clockIn: (data: {
    shift_name: string;
    shift_time: string;
    photo?: string;
    location?: string;
    notes?: string;
  }) => Promise<AttendanceRecord>;
  clockOut: (id: string, data: {
    photo?: string;
    location?: string;
    notes?: string;
  }) => Promise<AttendanceRecord | null>;
  
  // Staff Leave Management Actions
  submitLeaveRequest: (data: Omit<StaffLeaveRequest, 'id' | 'created_at' | 'status'>) => Promise<StaffLeaveRequest>;
  reviewLeaveRequest: (id: string, status: LeaveStatus, reviewerNote?: string, reviewerName?: string) => Promise<void>;

  // Board Governance & Decision Actions
  addBoardDiscussion: (data: Omit<BoardDiscussion, 'id' | 'created_at' | 'votes' | 'comments'>) => Promise<void>;
  castBoardVote: (discussionId: string, vote: 'approve' | 'reject' | 'abstain', comment?: string) => Promise<void>;
  addBoardComment: (discussionId: string, content: string) => Promise<void>;

  // Security Patrol Actions
  recordPatrolScan: (data: {
    checkpoint_id: string;
    round_number: number;
    round_time_slot: string;
    status: PatrolStatus;
    checklist_results: { [item: string]: boolean };
    note?: string;
    photo_url?: string;
    location_verified?: boolean;
  }) => Promise<PatrolScanLog | null>;
  
  // Supabase Sync
  isSupabaseConnected: boolean;
  isLoadingData: boolean;
  refreshData: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Initial default facilities in Narawadee Elegance
const INITIAL_FACILITIES: Facility[] = [
  {
    id: 'fac-1',
    name: 'สระว่ายน้ำระบบเกลือ (Infinity Edge Pool)',
    description: 'สระว่ายน้ำระบบเกลือขนาดโอลิมปิก พร้อมโซนจากุซซี่และสระเด็ก',
    open_time: '06:00',
    close_time: '21:00',
    max_capacity: 20,
    image_url: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=800&auto=format&fit=crop&q=80',
    rules: 'กรุณาสวมชุดว่ายน้ำที่ได้มาตรฐาน ล้างตัวก่อนลงสระทุกครั้ง',
  },
  {
    id: 'fac-2',
    name: 'ฟิตเนสคลับเฮาส์ (Elegance Fitness Center)',
    description: 'ห้องออกกำลังกายวิวสวน ครบครันด้วยเครื่องเล่น Cardio และ Weight Training',
    open_time: '06:00',
    close_time: '22:00',
    max_capacity: 15,
    image_url: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&auto=format&fit=crop&q=80',
    rules: 'กรุณาสวมรองเท้ากีฬา นำผ้าเช็ดตัวมาด้วย และทำความสะอาดอุปกรณ์หลังใช้งาน',
  },
  {
    id: 'fac-3',
    name: 'ห้องประชุมและ Co-Working Space สโมสร',
    description: 'ห้องประชุมปรับอากาศ พร้อมระบบจอสมาร์ททีวี รองรับการทำงานและจัดประชุม',
    open_time: '08:00',
    close_time: '20:00',
    max_capacity: 10,
    image_url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&auto=format&fit=crop&q=80',
    rules: 'ห้ามนำอาหารกลิ่นแรงเข้าห้องประชุม รักษาความสะอาดและปิดไฟเมื่อใช้งานเสร็จ',
  },
  {
    id: 'fac-4',
    name: 'สนามเทนนิส / แบดมินตันกลางแจ้ง',
    description: 'สนามมาตรฐานพื้นอะคริลิก พร้อมระบบไฟส่องสว่างยามค่ำคืน',
    open_time: '06:00',
    close_time: '21:00',
    max_capacity: 8,
    image_url: 'https://images.unsplash.com/photo-1622279457486-62dcc4a431d6?w=800&auto=format&fit=crop&q=80',
    rules: 'เปิดให้จองล่วงหน้า 1-3 วัน กรุณาสวมรองเท้าเทนนิสที่เหมาะสม',
  },
];

// Initial mock announcements
const INITIAL_ANNOUNCEMENTS: Announcement[] = [
  {
    id: 'ann-1',
    title: '📢 แจ้งกำหนดการฉีดพ่นยากันยุงและทำความสะอาดรางระบายน้ำรอบโครงการ',
    content: 'นิติบุคคล นราวดี อิลิแกนซ์ ขอเรียนแจ้งกำหนดการฉีดพ่นหมอกควันกำจัดยุงลายและลอกรางระบายน้ำประจำไตรมาส ในวันเสาร์ที่ 22 สิงหาคม 2569 เวลา 09.00 - 15.00 น. ขอความร่วมมือลูกบ้านปิดประตูหน้าต่างให้มิดชิด และเก็บสัตว์เลี้ยงไว้ในบ้าน',
    category: 'maintenance',
    is_pinned: true,
    author_name: 'ฝ่ายนิติบุคคล',
    created_at: new Date(Date.now() - 3600000 * 24 * 2).toISOString(),
    image_url: 'https://images.unsplash.com/photo-1584467735815-f778f274e296?w=800&auto=format&fit=crop&q=80',
  },
  {
    id: 'ann-2',
    title: '🌿 โครงการจัดเก็บขยะชิ้นใหญ่และกิ่งไม้ประจำเดือน',
    content: 'สำหรับลูกบ้านที่มีขยะชิ้นใหญ่ เฟอร์นิเจอร์เก่า หรือกิ่งไม้ สามารถนำมาวางไว้หน้าบ้านในคืนวันพฤหัสบดีที่ 27 สิงหาคม เพื่อให้รถเก็บขยะของเทศบาลเข้าจัดเก็บในเช้าวันศุกร์',
    category: 'general',
    is_pinned: false,
    author_name: 'ผู้จัดการนิติบุคคล',
    created_at: new Date(Date.now() - 3600000 * 24 * 5).toISOString(),
  },
  {
    id: 'ann-3',
    title: '🏊‍♂️ แจ้งปิดปรับปรุงระบบกรองสระว่ายน้ำชั่วคราว',
    content: 'สระว่ายน้ำสโมสรส่วนกลางจะปิดให้บริการในวันจันทร์ที่ 24 สิงหาคม เวลา 08.00 - 14.00 น. เพื่อทำการเปลี่ยนสารกรองแก้วและตรวจเช็คคุณภาพน้ำ',
    category: 'urgent',
    is_pinned: false,
    author_name: 'ฝ่ายช่างอาคาร',
    created_at: new Date(Date.now() - 3600000 * 12).toISOString(),
  }
];

// Initial mock parcels
const INITIAL_PARCELS: Parcel[] = [
  {
    id: 'par-1',
    tracking_number: 'TH1289472944',
    courier_name: 'Flash Express',
    house_number: '88/12',
    recipient_name: 'คุณอนุกูล พรหมดี',
    status: 'waiting',
    received_at: new Date(Date.now() - 3600000 * 3).toISOString(),
    image_url: 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=500&auto=format&fit=crop&q=80',
    note: 'กล่องขนาด M วางไว้ที่ชั้นวางพัสดุป้อม รปภ.1',
  },
  {
    id: 'par-2',
    tracking_number: 'SPXTH092837461',
    courier_name: 'Shopee Express',
    house_number: '88/12',
    recipient_name: 'คุณอนุกูล พรหมดี',
    status: 'waiting',
    received_at: new Date(Date.now() - 3600000 * 5).toISOString(),
    image_url: 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=500&auto=format&fit=crop&q=80',
    note: 'ซองเอกสาร/พัสดุขนาดเล็ก',
  },
  {
    id: 'par-3',
    tracking_number: 'KERRY90218844',
    courier_name: 'Kerry Express',
    house_number: '88/05',
    recipient_name: 'คุณวิภาดา สถิตย์',
    status: 'picked_up',
    received_at: new Date(Date.now() - 3600000 * 28).toISOString(),
    picked_up_at: new Date(Date.now() - 3600000 * 14).toISOString(),
    note: 'รับไปแล้วโดยเจ้าของบ้าน',
  },
  {
    id: 'par-4',
    tracking_number: 'ED89472847TH',
    courier_name: 'ไปรษณีย์ไทย (EMS)',
    house_number: '88/24',
    recipient_name: 'คุณสมชาย วิจิตรศิลป์',
    status: 'waiting',
    received_at: new Date(Date.now() - 3600000 * 8).toISOString(),
    note: 'กล่องพัสดุกล่องใหญ่',
  },
];

// Initial mock maintenance tickets
const INITIAL_TICKETS: MaintenanceTicket[] = [
  {
    id: 'tic-1',
    house_number: '88/12',
    reporter_name: 'คุณอนุกูล พรหมดี',
    title: 'โคมไฟส่องสว่างทางเท้าหน้าซอย 4 ดับ',
    category: 'electricity',
    description: 'หลอดไฟเสาไฟส่องสว่างดับตอนกลางคืน ทำให้ถนนมืดและเสี่ยงต่อความปลอดภัย รบกวนช่างเปลี่ยนหลอดไฟด้วยครับ',
    status: 'in_progress',
    priority: 'medium',
    created_at: new Date(Date.now() - 3600000 * 18).toISOString(),
    updated_at: new Date(Date.now() - 3600000 * 4).toISOString(),
    image_url: 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=600&auto=format&fit=crop&q=80',
    staff_notes: 'ฝ่ายช่างเบิกหลอด LED ใหม่เรียบร้อยแล้ว กำลังดำเนินการเปลี่ยนรอบบ่ายวันนี้',
    assigned_to: 'ช่างสมศักดิ์',
  },
  {
    id: 'tic-2',
    house_number: '88/09',
    reporter_name: 'คุณชลธิชา เจริญชัย',
    title: 'น้ำประปาไหลอ่อนผิดปกติช่วงหัวค่ำ',
    category: 'water',
    description: 'ช่วงเวลา 18.00-20.00 น. แรงดันน้ำประปาเบามาก ไม่แน่ใจมีท่อส่วนกลางรั่วหรือไม่',
    status: 'pending',
    priority: 'high',
    created_at: new Date(Date.now() - 3600000 * 6).toISOString(),
    updated_at: new Date(Date.now() - 3600000 * 6).toISOString(),
  },
  {
    id: 'tic-3',
    house_number: '88/12',
    reporter_name: 'คุณอนุกูล พรหมดี',
    title: 'ต้นไม้ส่วนกลางหน้าบ้านกิ่งไม้โน้มต่ำบังสายตา',
    category: 'landscape',
    description: 'กิ่งไม้ต้นประดู่เริ่มยื่นมาปิดทางเลี้ยวและโดนสายไฟ ขอให้คนสวนมาช่วยตัดแต่งกิ่งครับ',
    status: 'completed',
    priority: 'low',
    created_at: new Date(Date.now() - 3600000 * 72).toISOString(),
    updated_at: new Date(Date.now() - 3600000 * 20).toISOString(),
    staff_notes: 'ทีมคนสวนดำเนินการตัดแต่งกิ่งไม้เรียบร้อยแล้ว',
    assigned_to: 'ทีมงานสวน',
  }
];

// Initial mock bills
const INITIAL_BILLS: CommonFeeBill[] = [
  {
    id: 'bill-1',
    house_number: '88/12',
    resident_name: 'คุณอนุกูล พรหมดี',
    period: 'สิงหาคม 2569',
    amount: 1500,
    due_date: '31 ส.ค. 2569',
    status: 'unpaid',
  },
  {
    id: 'bill-2',
    house_number: '88/12',
    resident_name: 'คุณอนุกูล พรหมดี',
    period: 'กรกฎาคม 2569',
    amount: 1500,
    due_date: '31 ก.ค. 2569',
    status: 'paid',
    paid_at: new Date(Date.now() - 3600000 * 24 * 20).toISOString(),
    payment_method: 'Mobile Banking (PromptPay)',
    verified_by: 'คุณศศิธร (การเงินนิติ)',
    receipt_number: 'RC-256907-8812-1049',
    receipt_issued_at: new Date(Date.now() - 3600000 * 24 * 20 + 3600000).toISOString(),
    receipt_issued_by: 'คุณศศิธร สุขสมบูรณ์ (ผู้จัดการนิติบุคคล)',
  },
  {
    id: 'bill-3',
    house_number: '88/05',
    resident_name: 'คุณวิภาดา สถิตย์',
    period: 'สิงหาคม 2569',
    amount: 1800,
    due_date: '31 ส.ค. 2569',
    status: 'pending_verification',
    slip_url: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=500&auto=format&fit=crop&q=80',
    paid_at: new Date(Date.now() - 3600000 * 8).toISOString(),
    payment_method: 'PromptPay QR (KBANK)',
  },
  {
    id: 'bill-4',
    house_number: '88/20',
    resident_name: 'คุณประดิษฐ์ มั่นคง',
    period: 'กรกฎาคม 2569',
    amount: 1500,
    due_date: '31 ก.ค. 2569',
    status: 'overdue',
  },
];

// Initial mock visitor passes
const INITIAL_VISITORS: VisitorPass[] = [
  {
    id: 'vis-1',
    pass_code: '884920',
    host_house_number: '88/12',
    host_name: 'คุณอนุกูล พรหมดี',
    visitor_name: 'คุณธนากร (ช่างติดแอร์)',
    license_plate: '3กข-8921 กทม.',
    visit_date: new Date().toISOString().split('T')[0],
    purpose: 'งานซ่อมบำรุง / ช่างแอร์',
    status: 'approved',
    created_at: new Date().toISOString(),
  },
  {
    id: 'vis-2',
    pass_code: '192847',
    host_house_number: '88/12',
    host_name: 'คุณอนุกูล พรหมดี',
    visitor_name: 'คุณกิตติศักดิ์ บุญเรือง',
    license_plate: '2ขค-4451 นนทบุรี',
    visit_date: new Date().toISOString().split('T')[0],
    purpose: 'พบปะสังสรรค์ / ญาติ',
    status: 'checked_in',
    entry_time: new Date(Date.now() - 3600000 * 2).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }),
    created_at: new Date(Date.now() - 3600000 * 3).toISOString(),
  }
];

// Initial Security Patrol Checkpoints
const INITIAL_CHECKPOINTS: PatrolCheckpoint[] = [
  {
    id: 'cp-1',
    code: 'NRW-CP01',
    qr_data: 'NRW-ELEGANCE-CP01-GATE',
    name: 'ป้อม รปภ. หลัก & ไม้กั้นทางเข้า-ออกโครงการ',
    zone: 'ประตูทางเข้าหลัก',
    sequence: 1,
    description: 'จุดตรวจหน้าป้อม 1 ตรวจเช็คระบบไม้กั้นเปิด-ปิดอัตโนมัติ กล้อง CCTV และการคัดกรองรถเข้า-ออก',
    checklist_items: [
      'ระบบไม้กั้นเปิด-ปิดอัตโนมัติทำงานปกติ',
      'กล้อง CCTV สแกนทะเบียนชัดเจนครบทุกมุม',
      'ไฟส่องสว่างหน้าป้อมและป้ายโครงการติดครบ',
      'บันทึกสมุดรับรถและบัตรผู้มาติดต่อถูกต้อง',
    ],
    image_url: 'https://images.unsplash.com/photo-1541888946425-d0fbb18f1563?w=600&auto=format&fit=crop&q=80',
  },
  {
    id: 'cp-2',
    code: 'NRW-CP02',
    qr_data: 'NRW-ELEGANCE-CP02-CLUBHOUSE',
    name: 'อาคารคลับเฮาส์ & สระว่ายน้ำสโมสรส่วนกลาง',
    zone: 'โซนสโมสรส่วนกลาง',
    sequence: 2,
    description: 'ตรวจเช็คประตูล็อกอาคารสโมสร ความสะอาดรอบสระว่ายน้ำ และไฟส่องสว่างทางเดิน',
    checklist_items: [
      'ประตูลูกบิดและคีย์การ์ดสโมสรล็อกสนิท',
      'ไม่มีคนภายนอกบุกรุกพื้นที่สระหลังเวลาปิดบริการ',
      'ระบบไฟส่องสว่างและไฟใต้น้ำสระทำงานปกติ',
      'ความเรียบร้อยรอบฟิตเนสและห้องประชุม',
    ],
    image_url: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=600&auto=format&fit=crop&q=80',
  },
  {
    id: 'cp-3',
    code: 'NRW-CP03',
    qr_data: 'NRW-ELEGANCE-CP03-PARK',
    name: 'สวนสาธารณะส่วนกลาง & สนามเด็กเล่น',
    zone: 'สวนและลานกิจกรรม',
    sequence: 3,
    description: 'ตรวจความปลอดภัยบริเวณเครื่องเล่นเด็ก ม้านั่งพักผ่อน และลานวิ่งออกกำลังกาย',
    checklist_items: [
      'เครื่องเล่นเด็กและอุปกรณ์ออกกำลังกายอยู่ในสภาพแข็งแรงปลอดภัย',
      'ไม่มีสิ่งแปลกปลอมหรือกิ่งไม้กีดขวางทางวิ่ง',
      'โคมไฟส่องสว่างรอบสวนติดครบทุกดวง',
      'ไม่มีสัตว์เลื้อยคลานหรือสัตว์จรจัดในบริเวณสนามหญ้า',
    ],
    image_url: 'https://images.unsplash.com/photo-1588718704312-277ac1c77553?w=600&auto=format&fit=crop&q=80',
  },
  {
    id: 'cp-4',
    code: 'NRW-CP04',
    qr_data: 'NRW-ELEGANCE-CP04-NORTH',
    name: 'ซอย 1 ถึง ซอย 5 (บ้านเลขที่ 88/01 - 88/30 ฝั่งทิศเหนือ)',
    zone: 'ซอย 1-5 ฝั่งทิศเหนือ',
    sequence: 4,
    description: 'ตรวจเช็คความปลอดภัยตามแนวบ้านลูกบ้าน การจอดรถขวางทาง และไฟส่องสว่างหน้าซอย',
    checklist_items: [
      'ไม่มีรถยนต์จอดกีดขวางทางจราจรหรือหน้าบ้านลูกบ้าน',
      'โคมไฟส่องสว่างเสาไฟฟ้าหน้าซอยติดสว่างครบถ้วน',
      'ไม่มีบุคคลแปลกหน้าหรือมีพฤติกรรมน่าสงสัยเดินในซอย',
      'ประตูรั้วและหน้าบ้านลูกบ้านอยู่ในสภาพเรียบร้อย',
    ],
    image_url: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=600&auto=format&fit=crop&q=80',
  },
  {
    id: 'cp-5',
    code: 'NRW-CP05',
    qr_data: 'NRW-ELEGANCE-CP05-SOUTH',
    name: 'ซอย 6 ถึง ซอย 10 (บ้านเลขที่ 88/31 - 88/60 ฝั่งทิศใต้)',
    zone: 'ซอย 6-10 ฝั่งทิศใต้',
    sequence: 5,
    description: 'ตรวจเช็คความปลอดภัยตามแนวถนนฝั่งทิศใต้ ตรวจฝาท่อระบายน้ำ และการปิดล็อกบ้าน',
    checklist_items: [
      'ถนนทางวิ่งโล่ง ปลอดภัย ไม่มีการวางสิ่งของบนทางเท้า',
      'ฝาท่อระบายน้ำปิดสนิท ไม่มีทรุดหรือชำรุด',
      'ไฟส่องสว่างทางเดินและมุมอับติดสว่างชัดเจน',
      'ตรวจสอบบ้านที่ลูกบ้านฝากบ้านไว้กับ รปภ.',
    ],
    image_url: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&auto=format&fit=crop&q=80',
  },
  {
    id: 'cp-6',
    code: 'NRW-CP06',
    qr_data: 'NRW-ELEGANCE-CP06-PERIMETER',
    name: 'แนวรั้วรอบโครงการด้านหลัง & ประตูฉุกเฉิน',
    zone: 'แนวรั้วรอบนอกด้านหลัง',
    sequence: 6,
    description: 'ตรวจเช็ครั้วคอนกรีตสูง ลวดหนามกันขโมย ประตูหนีไฟ และจุดเสี่ยงติดพื้นที่ภายนอก',
    checklist_items: [
      'แนวรั้วคอนกรีตและลวดหนามไม่มีร่องรอยปีนป่ายหรือถูกตัด',
      'ประตูหนีไฟและประตูทางออกฉุกเฉินล็อกแน่นหนา',
      'ไฟสปอตไลท์ส่องแนวรั้วทำงานปกติทุกจุด',
      'ไม่มีกิ่งไม้จากภายนอกพาดหรือกดทับแนวสายไฟ',
    ],
    image_url: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=600&auto=format&fit=crop&q=80',
  },
  {
    id: 'cp-7',
    code: 'NRW-CP07',
    qr_data: 'NRW-ELEGANCE-CP07-UTILITY',
    name: 'สถานีควบคุมระบบไฟฟ้า MDB & อาคารปั๊มน้ำส่วนกลาง',
    zone: 'โซนระบบสาธารณูปโภค',
    sequence: 7,
    description: 'ตรวจเช็คตู้ควบคุมไฟฟ้าหลัก ปั๊มน้ำแรงดันสูง และอุปกรณ์ป้องกันอัคคีภัย',
    checklist_items: [
      'ตู้ควบคุมไฟฟ้า Main Distribution Board (MDB) ล็อกมิดชิด',
      'ปั๊มน้ำส่วนกลางทำงานปกติ ไม่มีเสียงเตือน Alarm หรือแรงดันตก',
      'ไม่มีการรั่วซึมของน้ำหรือกลิ่นไหม้ในห้องควบคุม',
      'ถังดับเพลิงอยู่ในจุดติดตั้งและพร้อมใช้งาน (เกจวัดสีเขียว)',
    ],
    image_url: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=600&auto=format&fit=crop&q=80',
  },
];

// Initial mock staff attendance records
const INITIAL_ATTENDANCE: AttendanceRecord[] = [
  {
    id: 'att-1',
    user_id: 'staff-01',
    staff_name: 'รปภ. สมศักดิ์ มั่งมี',
    department: 'ฝ่ายรักษาความปลอดภัย',
    role_title: 'หัวหน้าชุด รปภ.',
    date: new Date().toISOString().split('T')[0],
    shift_name: 'กะเช้า (Morning Shift)',
    shift_time: '07:00 - 19:00 น.',
    clock_in_time: '06:52 น.',
    clock_in_location: 'ป้อม รปภ. หลัก ประตู 1 (GPS Verified)',
    clock_in_photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80',
    clock_in_status: 'on_time',
    notes: 'เข้าประจำการผลัดเช้า ตรวจความพร้อมอาวุธและวิทยุสื่อสารครบถ้วน',
    created_at: new Date(Date.now() - 3600000 * 5).toISOString(),
  },
  {
    id: 'att-2',
    user_id: 'staff-02',
    staff_name: 'นายช่าง สมศักดิ์ ชำนาญการ',
    department: 'ฝ่ายช่างและซ่อมบำรุง',
    role_title: 'ช่างเทคนิคอาคาร',
    date: new Date().toISOString().split('T')[0],
    shift_name: 'เวลาทำการนิติ (Office Shift)',
    shift_time: '08:30 - 17:30 น.',
    clock_in_time: '08:15 น.',
    clock_in_location: 'สำนักงานนิติบุคคล ชั้น 1 (GPS Verified)',
    clock_in_photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&auto=format&fit=crop&q=80',
    clock_in_status: 'on_time',
    notes: 'เบิกอุปกรณ์สำหรับงานเปลี่ยนหลอดไฟถนนซอย 4',
    created_at: new Date(Date.now() - 3600000 * 3.5).toISOString(),
  },
  {
    id: 'att-3',
    user_id: 'staff-03',
    staff_name: 'รปภ. วิชัย การ์ดมือดี',
    department: 'ฝ่ายรักษาความปลอดภัย',
    role_title: 'เจ้าหน้าที่ตรวจเวร',
    date: new Date(Date.now() - 86400000).toISOString().split('T')[0],
    shift_name: 'กะดึก (Night Shift)',
    shift_time: '19:00 - 07:00 น.',
    clock_in_time: '18:50 น.',
    clock_in_location: 'ป้อม รปภ. หลัก ประตู 1 (GPS Verified)',
    clock_in_status: 'on_time',
    clock_out_time: '07:05 น.',
    clock_out_location: 'ป้อม รปภ. หลัก ประตู 1',
    clock_out_status: 'normal',
    work_duration_hours: 12.2,
    notes: 'ส่งมอบเวรเช้า เหตุการณ์ปกติ 100%',
    created_at: new Date(Date.now() - 86400000).toISOString(),
  },
  {
    id: 'att-4',
    user_id: 'admin-01',
    staff_name: 'คุณศศิธร เจริญสุข',
    department: 'ฝ่ายบริหารจัดการนิติบุคคล',
    role_title: 'ผู้จัดการนิติบุคคล',
    date: new Date().toISOString().split('T')[0],
    shift_name: 'เวลาทำการนิติ (Office Shift)',
    shift_time: '08:30 - 17:30 น.',
    clock_in_time: '08:24 น.',
    clock_in_location: 'สำนักงานนิติบุคคล อาคารสโมสร (GPS Verified)',
    clock_in_status: 'on_time',
    notes: 'เตรียมเอกสารสรุปยอดชำระค่าส่วนกลางประจำเดือน',
    created_at: new Date(Date.now() - 3600000 * 3.2).toISOString(),
  }
];

// Initial mock patrol scan logs
const INITIAL_PATROL_LOGS: PatrolScanLog[] = [
  {
    id: 'scan-1',
    checkpoint_id: 'cp-1',
    checkpoint_code: 'NRW-CP01',
    checkpoint_name: 'ป้อม รปภ. หลัก & ไม้กั้นทางเข้า-ออกโครงการ',
    checkpoint_zone: 'ประตูทางเข้าหลัก',
    officer_id: 'staff-01',
    officer_name: 'รปภ. สมศักดิ์ มั่งมี',
    round_number: 1,
    round_time_slot: 'รอบเช้า 08:00 น.',
    scanned_at: new Date(Date.now() - 3600000 * 3).toISOString(),
    scanned_time: '08:05 น.',
    status: 'normal',
    checklist_results: {
      'ระบบไม้กั้นเปิด-ปิดอัตโนมัติทำงานปกติ': true,
      'กล้อง CCTV สแกนทะเบียนชัดเจนครบทุกมุม': true,
      'ไฟส่องสว่างหน้าป้อมและป้ายโครงการติดครบ': true,
      'บันทึกสมุดรับรถและบัตรผู้มาติดต่อถูกต้อง': true,
    },
    note: 'สภาพการจราจรหน้าป้อมปกติ ไม้กั้นตอบสนองเร็ว',
    location_verified: true,
  },
  {
    id: 'scan-2',
    checkpoint_id: 'cp-2',
    checkpoint_code: 'NRW-CP02',
    checkpoint_name: 'อาคารคลับเฮาส์ & สระว่ายน้ำสโมสรส่วนกลาง',
    checkpoint_zone: 'โซนสโมสรส่วนกลาง',
    officer_id: 'staff-01',
    officer_name: 'รปภ. สมศักดิ์ มั่งมี',
    round_number: 1,
    round_time_slot: 'รอบเช้า 08:00 น.',
    scanned_at: new Date(Date.now() - 3600000 * 2.7).toISOString(),
    scanned_time: '08:22 น.',
    status: 'normal',
    checklist_results: {
      'ประตูลูกบิดและคีย์การ์ดสโมสรล็อกสนิท': true,
      'ไม่มีคนภายนอกบุกรุกพื้นที่สระหลังเวลาปิดบริการ': true,
      'ระบบไฟส่องสว่างและไฟใต้น้ำสระทำงานปกติ': true,
      'ความเรียบร้อยรอบฟิตเนสและห้องประชุม': true,
    },
    note: 'สระว่ายน้ำสะอาด มีลูกบ้านมาใช้บริการ 2 ท่าน',
    location_verified: true,
  },
  {
    id: 'scan-3',
    checkpoint_id: 'cp-3',
    checkpoint_code: 'NRW-CP03',
    checkpoint_name: 'สวนสาธารณะส่วนกลาง & สนามเด็กเล่น',
    checkpoint_zone: 'สวนและลานกิจกรรม',
    officer_id: 'staff-01',
    officer_name: 'รปภ. สมศักดิ์ มั่งมี',
    round_number: 1,
    round_time_slot: 'รอบเช้า 08:00 น.',
    scanned_at: new Date(Date.now() - 3600000 * 2.4).toISOString(),
    scanned_time: '08:38 น.',
    status: 'normal',
    checklist_results: {
      'เครื่องเล่นเด็กและอุปกรณ์ออกกำลังกายอยู่ในสภาพแข็งแรงปลอดภัย': true,
      'ไม่มีสิ่งแปลกปลอมหรือกิ่งไม้กีดขวางทางวิ่ง': true,
      'โคมไฟส่องสว่างรอบสวนติดครบทุกดวง': true,
      'ไม่มีสัตว์เลื้อยคลานหรือสัตว์จรจัดในบริเวณสนามหญ้า': true,
    },
    note: 'ทางวิ่งสะอาด เรียบร้อยดี',
    location_verified: true,
  },
  {
    id: 'scan-4',
    checkpoint_id: 'cp-4',
    checkpoint_code: 'NRW-CP04',
    checkpoint_name: 'ซอย 1 ถึง ซอย 5 (บ้านเลขที่ 88/01 - 88/30 ฝั่งทิศเหนือ)',
    checkpoint_zone: 'ซอย 1-5 ฝั่งทิศเหนือ',
    officer_id: 'staff-01',
    officer_name: 'รปภ. สมศักดิ์ มั่งมี',
    round_number: 1,
    round_time_slot: 'รอบเช้า 08:00 น.',
    scanned_at: new Date(Date.now() - 3600000 * 2.1).toISOString(),
    scanned_time: '08:55 น.',
    status: 'abnormal',
    checklist_results: {
      'ไม่มีรถยนต์จอดกีดขวางทางจราจรหรือหน้าบ้านลูกบ้าน': true,
      'โคมไฟส่องสว่างเสาไฟฟ้าหน้าซอยติดสว่างครบถ้วน': false,
      'ไม่มีบุคคลแปลกหน้าหรือมีพฤติกรรมน่าสงสัยเดินในซอย': true,
      'ประตูรั้วและหน้าบ้านลูกบ้านอยู่ในสภาพเรียบร้อย': true,
    },
    note: 'พบโคมไฟเสาไฟฟ้าต้นหน้าซอย 4 ดับ (แจ้งช่างอาคารเปิดใบงานซ่อมแล้ว)',
    photo_url: 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=600&auto=format&fit=crop&q=80',
    location_verified: true,
  }
];

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedRole, setSelectedRole] = useState<UserRole>('resident');
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [authReady, setAuthReady] = useState(false);

  const [announcements, setAnnouncements] = useState<Announcement[]>(() => {
    const saved = localStorage.getItem('narawadee_announcements');
    return saved ? JSON.parse(saved) : INITIAL_ANNOUNCEMENTS;
  });

  const [parcels, setParcels] = useState<Parcel[]>(() => {
    const saved = localStorage.getItem('narawadee_parcels');
    return saved ? JSON.parse(saved) : INITIAL_PARCELS;
  });

  const [maintenanceTickets, setMaintenanceTickets] = useState<MaintenanceTicket[]>(() => {
    const saved = localStorage.getItem('narawadee_tickets');
    return saved ? JSON.parse(saved) : INITIAL_TICKETS;
  });

  const [bills, setBills] = useState<CommonFeeBill[]>(() => {
    const saved = localStorage.getItem('narawadee_bills');
    return saved ? JSON.parse(saved) : INITIAL_BILLS;
  });

  const [visitorPasses, setVisitorPasses] = useState<VisitorPass[]>(() => {
    const saved = localStorage.getItem('narawadee_visitors');
    return saved ? JSON.parse(saved) : INITIAL_VISITORS;
  });

  const [facilities] = useState<Facility[]>(INITIAL_FACILITIES);
  const [patrolCheckpoints] = useState<PatrolCheckpoint[]>(INITIAL_CHECKPOINTS);

  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>(() => {
    const saved = localStorage.getItem('narawadee_attendance');
    return saved ? JSON.parse(saved) : INITIAL_ATTENDANCE;
  });

  const [patrolLogs, setPatrolLogs] = useState<PatrolScanLog[]>(() => {
    const saved = localStorage.getItem('narawadee_patrol_logs');
    return saved ? JSON.parse(saved) : INITIAL_PATROL_LOGS;
  });

  const [residents, setResidents] = useState<ResidentDirectoryItem[]>(() => {
    const saved = localStorage.getItem('narawadee_residents');
    return saved ? JSON.parse(saved) : INITIAL_RESIDENTS;
  });

  const [leaveRequests, setLeaveRequests] = useState<StaffLeaveRequest[]>(() => {
    const saved = localStorage.getItem('narawadee_leave_requests');
    return saved ? JSON.parse(saved) : INITIAL_LEAVE_REQUESTS;
  });

  const [leaveQuotas, setLeaveQuotas] = useState<StaffLeaveQuota[]>(() => {
    const saved = localStorage.getItem('narawadee_leave_quotas');
    return saved ? JSON.parse(saved) : INITIAL_LEAVE_QUOTAS;
  });

  const [boardDiscussions, setBoardDiscussions] = useState<BoardDiscussion[]>(() => {
    const saved = localStorage.getItem('narawadee_board_discussions');
    return saved ? JSON.parse(saved) : INITIAL_BOARD_DISCUSSIONS;
  });

  const [financialMetrics] = useState<FinancialMonthMetric[]>(INITIAL_FINANCIAL_METRICS);

  const [bookings, setBookings] = useState<FacilityBooking[]>(() => {
    const saved = localStorage.getItem('narawadee_bookings');
    return saved ? JSON.parse(saved) : [
      {
        id: 'book-1',
        facility_id: 'fac-1',
        facility_name: 'สระว่ายน้ำระบบเกลือ (Infinity Edge Pool)',
        house_number: '88/12',
        resident_name: 'คุณอนุกูล พรหมดี',
        booking_date: new Date().toISOString().split('T')[0],
        time_slot: '17:00 - 18:30 น.',
        status: 'confirmed',
        created_at: new Date().toISOString(),
      }
    ];
  });

  const [isSupabaseConnected, setIsSupabaseConnected] = useState<boolean>(false);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);

  // The Supabase session is the source of truth. Never restore identity from localStorage.
  useEffect(() => {
    localStorage.removeItem('narawadee_current_user');
    const client = getSupabaseClient();
    if (!client) {
      setAuthReady(true);
      return;
    }

    let active = true;
    let lineEnrollmentInFlight: Promise<string | null> | null = null;
    const isPasswordRecoveryFlow = () => {
      const query = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      return sessionStorage.getItem('narawadee_password_recovery') === 'true'
        || query.get('reset') === '1'
        || query.get('type') === 'recovery'
        || hash.get('type') === 'recovery';
    };
    const loadProfile = async (session: { user: { id: string; email?: string } } | null) => {
      if (isPasswordRecoveryFlow()) {
        if (active) setCurrentUser(null);
        return;
      }
      if (!session) {
        if (active) setCurrentUser(null);
        return;
      }
      const { data, error } = await client
        .from('profiles')
        .select('id,email,full_name,role,house_number,phone,department,avatar_url,created_at,account_status')
        .eq('auth_user_id', session.user.id)
        .maybeSingle();
      if (!active) return;
      let profile = data;
      if (!error && !profile && typeof window !== 'undefined') {
        const pending = sessionStorage.getItem('pending_line_registration');
        if (pending) {
          try {
            const registration = JSON.parse(pending) as { fullName: string; houseNumber: string; phone: string; email: string; inviteCode: string };
            lineEnrollmentInFlight ??= (async () => {
              const { error: registrationError } = await client.rpc('register_resident_with_invite', {
                p_code: registration.inviteCode,
                p_house_number: registration.houseNumber,
                p_full_name: registration.fullName,
                p_phone: registration.phone,
                p_email: registration.email,
              });
              if (!registrationError) return null;
              if (registrationError.message.includes('invalid_or_expired_invite')) return 'รหัสเชิญไม่ถูกต้อง หมดอายุ หรือถูกใช้แล้ว';
              if (registrationError.message.includes('invite_house_mismatch')) return 'เลขที่บ้านไม่ตรงกับรหัสเชิญ กรุณาตรวจสอบกับนิติบุคคล';
              return 'ลงทะเบียน LINE ไม่สำเร็จ กรุณาตรวจสอบข้อมูลและลองอีกครั้ง';
            })();
            const enrollmentError = await lineEnrollmentInFlight;
            if (enrollmentError) {
              sessionStorage.removeItem('pending_line_registration');
              sessionStorage.setItem('resident_registration_error', enrollmentError);
              await client.auth.signOut();
              return;
            }
            sessionStorage.removeItem('pending_line_registration');
            const { data: createdProfile, error: reloadError } = await client
              .from('profiles')
              .select('id,email,full_name,role,house_number,phone,department,avatar_url,created_at,account_status')
              .eq('auth_user_id', session.user.id)
              .maybeSingle();
            if (reloadError) { await client.auth.signOut(); return; }
            profile = createdProfile;
          } catch {
            sessionStorage.removeItem('pending_line_registration');
            await client.auth.signOut();
            return;
          }
        }
      }
      if (error || !profile || profile.account_status !== 'active') {
        setCurrentUser(null);
        if (profile?.account_status === 'pending') {
          sessionStorage.setItem('resident_registration_error', 'ลงทะเบียนแล้ว กรุณารอผู้ดูแลอนุมัติบัญชีก่อนเข้าใช้งาน');
        }
        await client.auth.signOut();
        return;
      }
      setCurrentUser({ ...profile, role: profile.role as UserRole });
      setSelectedRole(profile.role as UserRole);
    };

    client.auth.getSession().then(({ data }) => {
      if (isPasswordRecoveryFlow()) setCurrentUser(null);
      else void loadProfile(data.session);
    }).finally(() => {
      if (active) setAuthReady(true);
    });
    const { data: { subscription } } = client.auth.onAuthStateChange((event, session) => {
      // Keep password-recovery links on the reset form until the user chooses a new password.
      if (event === 'PASSWORD_RECOVERY') {
        sessionStorage.setItem('narawadee_password_recovery', 'true');
        setCurrentUser(null);
        return;
      }
      if (isPasswordRecoveryFlow()) {
        setCurrentUser(null);
        return;
      }
      window.setTimeout(() => { void loadProfile(session); }, 0);
    });
    return () => { active = false; subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    localStorage.setItem('narawadee_announcements', JSON.stringify(announcements));
  }, [announcements]);

  useEffect(() => {
    localStorage.setItem('narawadee_parcels', JSON.stringify(parcels));
  }, [parcels]);

  useEffect(() => {
    localStorage.setItem('narawadee_tickets', JSON.stringify(maintenanceTickets));
  }, [maintenanceTickets]);

  useEffect(() => {
    localStorage.setItem('narawadee_bills', JSON.stringify(bills));
  }, [bills]);

  useEffect(() => {
    localStorage.setItem('narawadee_visitors', JSON.stringify(visitorPasses));
  }, [visitorPasses]);

  useEffect(() => {
    localStorage.setItem('narawadee_bookings', JSON.stringify(bookings));
  }, [bookings]);

  useEffect(() => {
    localStorage.setItem('narawadee_attendance', JSON.stringify(attendanceRecords));
  }, [attendanceRecords]);

  useEffect(() => {
    localStorage.setItem('narawadee_patrol_logs', JSON.stringify(patrolLogs));
  }, [patrolLogs]);

  useEffect(() => {
    localStorage.setItem('narawadee_residents', JSON.stringify(residents));
  }, [residents]);

  useEffect(() => {
    localStorage.setItem('narawadee_leave_requests', JSON.stringify(leaveRequests));
  }, [leaveRequests]);

  useEffect(() => {
    localStorage.setItem('narawadee_leave_quotas', JSON.stringify(leaveQuotas));
  }, [leaveQuotas]);

  useEffect(() => {
    localStorage.setItem('narawadee_board_discussions', JSON.stringify(boardDiscussions));
  }, [boardDiscussions]);

  // Initial Supabase check and load
  const refreshData = async () => {
    if (!currentUser) {
      setIsSupabaseConnected(false);
      return;
    }
    const config = getStoredConfig();
    if (!config.isConfigured) {
      setIsSupabaseConnected(false);
      return;
    }

    const client = getSupabaseClient();
    if (!client) {
      setIsSupabaseConnected(false);
      return;
    }

    // Do not show cached demo data to an authenticated user while live data loads.
    setAnnouncements([]);
    setParcels([]);
    setMaintenanceTickets([]);
    setBills([]);
    setVisitorPasses([]);
    setBookings([]);
    setAttendanceRecords([]);
    setPatrolLogs([]);
    setResidents([]);
    setLeaveRequests([]);
    setBoardDiscussions([]);
    setIsLoadingData(true);
    try {
      // Fetch Announcements
      const { data: annData, error: annErr } = await client.from('announcements').select('*').order('created_at', { ascending: false });
      if (!annErr && annData && annData.length > 0) {
        setAnnouncements(annData);
      }

      // Fetch Parcels
      const { data: parcData, error: parcErr } = await client.from('parcels').select('*').order('received_at', { ascending: false });
      if (!parcErr && parcData && parcData.length > 0) {
        setParcels(parcData);
      }

      // Fetch Tickets
      const { data: ticData, error: ticErr } = await client.from('maintenance_tickets').select('*').order('created_at', { ascending: false });
      if (!ticErr && ticData && ticData.length > 0) {
        setMaintenanceTickets(ticData);
      }

      // Fetch Bills
      const { data: billData, error: billErr } = await client.from('bills').select('*').order('due_date', { ascending: false });
      if (!billErr && billData && billData.length > 0) {
        setBills(billData);
      }

      // Fetch Visitors
      const { data: visData, error: visErr } = await client.from('visitors').select('*').order('created_at', { ascending: false });
      if (!visErr && visData && visData.length > 0) {
        setVisitorPasses(visData);
      }

      // Fetch Bookings
      const { data: bookData, error: bookErr } = await client.from('facility_bookings').select('*').order('created_at', { ascending: false });
      if (!bookErr && bookData && bookData.length > 0) {
        setBookings(bookData);
      }

      // Fetch Attendance Records
      const { data: attData, error: attErr } = await client.from('attendance_records').select('*').order('created_at', { ascending: false });
      if (!attErr && attData && attData.length > 0) {
        setAttendanceRecords(attData);
      }

      // Fetch Patrol Logs
      const { data: patrolData, error: patrolErr } = await client.from('patrol_scan_logs').select('*').order('scanned_at', { ascending: false });
      if (!patrolErr && patrolData && patrolData.length > 0) {
        setPatrolLogs(patrolData);
      }

      // **เรียกดึงข้อมูลจากตาราง residents (ไม่ใช่ profiles) - จัดเก็บข้อมูลลูกบ้านจริงๆ**
      const { data: resData, error: resErr } = await client.from('residents').select('*').order('created_at', { ascending: false });
      if (!resErr && resData && resData.length > 0) {
        setResidents(resData as any);
      }

      setIsSupabaseConnected(true);
    } catch (e) {
      console.log('Supabase sync notice:', e);
      setIsSupabaseConnected(false);
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    void refreshData();
  }, [currentUser?.id]);

  const login = (profile: UserProfile) => {
    setCurrentUser(profile);
    setSelectedRole(profile.role);
  };

  const logout = async () => {
    const client = getSupabaseClient();
    if (client) await client.auth.signOut();
    setCurrentUser(null);
  };

  // Actions
  const addAnnouncement = async (item: Omit<Announcement, 'id' | 'created_at'>) => {
    const newAnn: Announcement = {
      ...item,
      id: 'ann-' + Date.now(),
      created_at: new Date().toISOString(),
    };

    setAnnouncements((prev) => [newAnn, ...prev]);

    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('announcements').insert([{
          title: item.title,
          content: item.content,
          category: item.category,
          is_pinned: item.is_pinned || false,
          author_name: item.author_name,
          image_url: item.image_url,
        }]);
      } catch (err) {
        console.warn('Supabase insert announcement fallback:', err);
      }
    }
  };

  const deleteAnnouncement = async (id: string) => {
    setAnnouncements((prev) => prev.filter((a) => a.id !== id));
    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('announcements').delete().eq('id', id);
      } catch (err) {
        console.warn('Supabase delete announcement:', err);
      }
    }
  };

  const addParcel = async (item: Omit<Parcel, 'id' | 'received_at' | 'status'>) => {
    const newParcel: Parcel = {
      ...item,
      id: 'par-' + Date.now(),
      status: 'waiting',
      received_at: new Date().toISOString(),
    };

    setParcels((prev) => [newParcel, ...prev]);

    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('parcels').insert([{
          tracking_number: item.tracking_number,
          courier_name: item.courier_name,
          house_number: item.house_number,
          recipient_name: item.recipient_name,
          status: 'waiting',
          image_url: item.image_url,
          note: item.note,
        }]);
      } catch (err) {
        console.warn('Supabase add parcel fallback:', err);
      }
    }
  };

  const markParcelPickedUp = async (id: string) => {
    const pickedTime = new Date().toISOString();
    setParcels((prev) =>
      prev.map((p) => (p.id === id ? { ...p, status: 'picked_up', picked_up_at: pickedTime } : p))
    );

    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('parcels').update({ status: 'picked_up', picked_up_at: pickedTime }).eq('id', id);
      } catch (err) {
        console.warn('Supabase mark parcel picked up:', err);
      }
    }
  };

  const createMaintenanceTicket = async (item: Omit<MaintenanceTicket, 'id' | 'created_at' | 'updated_at' | 'status'>) => {
    const now = new Date().toISOString();
    const newTicket: MaintenanceTicket = {
      ...item,
      id: 'tic-' + Date.now(),
      status: 'pending',
      created_at: now,
      updated_at: now,
    };

    setMaintenanceTickets((prev) => [newTicket, ...prev]);

    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('maintenance_tickets').insert([{
          house_number: item.house_number,
          reporter_name: item.reporter_name,
          title: item.title,
          category: item.category,
          description: item.description,
          priority: item.priority,
          image_url: item.image_url,
          status: 'pending',
        }]);
      } catch (err) {
        console.warn('Supabase create maintenance ticket:', err);
      }
    }
  };

  const updateMaintenanceStatus = async (
    id: string,
    status: MaintenanceTicket['status'],
    staffNotes?: string,
    assignedTo?: string
  ) => {
    const now = new Date().toISOString();
    setMaintenanceTickets((prev) =>
      prev.map((t) => {
        if (t.id === id) {
          return {
            ...t,
            status,
            staff_notes: staffNotes !== undefined ? staffNotes : t.staff_notes,
            assigned_to: assignedTo !== undefined ? assignedTo : t.assigned_to,
            updated_at: now,
          };
        }
        return t;
      })
    );

    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('maintenance_tickets').update({
          status,
          staff_notes: staffNotes,
          assigned_to: assignedTo,
          updated_at: now,
        }).eq('id', id);
      } catch (err) {
        console.warn('Supabase update maintenance status:', err);
      }
    }
  };

  const payBill = async (id: string, slipUrl: string) => {
    const now = new Date().toISOString();
    setBills((prev) =>
      prev.map((b) => (b.id === id ? { ...b, status: 'pending_verification', slip_url: slipUrl, paid_at: now } : b))
    );

    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('bills').update({
          status: 'pending_verification',
          slip_url: slipUrl,
          paid_at: now,
        }).eq('id', id);
      } catch (err) {
        console.warn('Supabase pay bill:', err);
      }
    }
  };

  const verifyBill = async (id: string, status: 'paid' | 'overdue', verifiedBy: string): Promise<CommonFeeBill | null> => {
    const now = new Date().toISOString();
    let updatedBillResult: CommonFeeBill | null = null;

    setBills((prev) =>
      prev.map((b) => {
        if (b.id === id) {
          const houseClean = b.house_number.replace(/[^0-9]/g, '');
          const randomSuffix = Math.floor(1000 + Math.random() * 9000);
          const generatedReceiptNum = b.receipt_number || `RC-256908-${houseClean || '88'}-${randomSuffix}`;
          
          updatedBillResult = {
            ...b,
            status,
            verified_by: verifiedBy,
            payment_method: b.payment_method || 'Mobile Banking (PromptPay)',
            paid_at: b.paid_at || now,
            receipt_number: status === 'paid' ? generatedReceiptNum : undefined,
            receipt_issued_at: status === 'paid' ? now : undefined,
            receipt_issued_by: status === 'paid' ? `${verifiedBy} (นิติบุคคล นราวดี อิลิแกนซ์)` : undefined,
          };
          return updatedBillResult;
        }
        return b;
      })
    );

    // Also update resident common fee status if paid
    const targetBill = bills.find(b => b.id === id);
    if (targetBill && status === 'paid') {
      setResidents(prev => prev.map(r => r.house_number === targetBill.house_number ? { ...r, common_fee_status: 'paid', outstanding_amount: 0 } : r));
    }

    const client = getSupabaseClient();
    if (client && updatedBillResult) {
      try {
        await client.from('bills').update({
          status,
          verified_by: verifiedBy,
          receipt_number: (updatedBillResult as CommonFeeBill).receipt_number,
          receipt_issued_at: (updatedBillResult as CommonFeeBill).receipt_issued_at,
          receipt_issued_by: (updatedBillResult as CommonFeeBill).receipt_issued_by,
        }).eq('id', id);
      } catch (err) {
        console.warn('Supabase verify bill:', err);
      }
    }

    return updatedBillResult;
  };

  const rejectBill = async (id: string, reason: string, verifiedBy: string) => {
    setBills((prev) =>
      prev.map((b) =>
        b.id === id
          ? {
              ...b,
              status: 'rejected',
              rejection_reason: reason,
              verified_by: verifiedBy,
            }
          : b
      )
    );

    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('bills').update({
          status: 'rejected',
          rejection_reason: reason,
          verified_by: verifiedBy,
        }).eq('id', id);
      } catch (err) {
        console.warn('Supabase reject bill:', err);
      }
    }
  };

  // Resident Directory Actions
  const addResident = async (resident: Omit<ResidentDirectoryItem, 'id'>) => {
    const newResident: ResidentDirectoryItem = {
      ...resident,
      id: 'res-' + Date.now(),
    };
    setResidents((prev) => [newResident, ...prev]);
  };

  const updateResident = async (id: string, data: Partial<ResidentDirectoryItem>) => {
    setResidents((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...data } : r))
    );
  };

  // Staff Leave Actions
  const submitLeaveRequest = async (data: Omit<StaffLeaveRequest, 'id' | 'created_at' | 'status'>): Promise<StaffLeaveRequest> => {
    const newReq: StaffLeaveRequest = {
      ...data,
      id: 'leave-' + Date.now(),
      status: 'pending',
      created_at: new Date().toISOString(),
    };

    setLeaveRequests((prev) => [newReq, ...prev]);
    return newReq;
  };

  const reviewLeaveRequest = async (
    id: string,
    status: LeaveStatus,
    reviewerNote?: string,
    reviewerName?: string
  ) => {
    const now = new Date().toISOString();
    const req = leaveRequests.find(r => r.id === id);

    setLeaveRequests((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              status,
              reviewed_by: reviewerName || currentUser?.full_name || 'ผู้จัดการนิติบุคคล',
              reviewer_note: reviewerNote,
              reviewed_at: now,
            }
          : r
      )
    );

    // If approved, update leave quota
    if (status === 'approved' && req) {
      setLeaveQuotas((prev) =>
        prev.map((q) => {
          if (q.staff_id === req.staff_id) {
            if (req.leave_type === 'sick') {
              return { ...q, sick_used: q.sick_used + req.total_days };
            } else if (req.leave_type === 'personal') {
              return { ...q, personal_used: q.personal_used + req.total_days };
            } else if (req.leave_type === 'vacation') {
              return { ...q, vacation_used: q.vacation_used + req.total_days };
            }
          }
          return q;
        })
      );
    }
  };

  // Board Governance Actions
  const addBoardDiscussion = async (data: Omit<BoardDiscussion, 'id' | 'created_at' | 'votes' | 'comments'>) => {
    const newDisc: BoardDiscussion = {
      ...data,
      id: 'board-' + Date.now(),
      created_at: new Date().toISOString(),
      votes: [],
      comments: [],
    };
    setBoardDiscussions((prev) => [newDisc, ...prev]);
  };

  const castBoardVote = async (
    discussionId: string,
    vote: 'approve' | 'reject' | 'abstain',
    comment?: string
  ) => {
    const voterId = currentUser?.id || 'board-member-' + Date.now();
    const voterName = currentUser?.full_name || 'กรรมการผู้ทรงคุณวุฒิ';
    const voterRole = currentUser?.role === 'admin' ? 'กรรมการนิติบุคคล' : 'กรรมการตัวแทนลูกบ้าน';
    const now = new Date().toISOString();

    setBoardDiscussions((prev) =>
      prev.map((disc) => {
        if (disc.id === discussionId) {
          // Check if already voted
          const existingVoteIdx = disc.votes.findIndex((v) => v.voter_id === voterId);
          let newVotes = [...disc.votes];
          const newVoteEntry: BoardVote = {
            voter_id: voterId,
            voter_name: voterName,
            voter_role: voterRole,
            vote,
            voted_at: now,
            comment,
          };

          if (existingVoteIdx >= 0) {
            newVotes[existingVoteIdx] = newVoteEntry;
          } else {
            newVotes.push(newVoteEntry);
          }

          let newComments = [...disc.comments];
          if (comment) {
            newComments.push({
              id: 'comm-' + Date.now(),
              author_name: voterName,
              author_role: voterRole,
              content: comment,
              created_at: now,
            });
          }

          return {
            ...disc,
            votes: newVotes,
            comments: newComments,
          };
        }
        return disc;
      })
    );
  };

  const addBoardComment = async (discussionId: string, content: string) => {
    const authorName = currentUser?.full_name || 'กรรมการผู้ทรงคุณวุฒิ';
    const authorRole = currentUser?.role === 'admin' ? 'ฝ่ายบริหารนิติบุคคล' : 'กรรมการลูกบ้าน';
    const newComm: BoardComment = {
      id: 'comm-' + Date.now(),
      author_name: authorName,
      author_role: authorRole,
      content,
      created_at: new Date().toISOString(),
    };

    setBoardDiscussions((prev) =>
      prev.map((d) => (d.id === discussionId ? { ...d, comments: [...d.comments, newComm] } : d))
    );
  };

  const createVisitorPass = async (item: Omit<VisitorPass, 'id' | 'created_at' | 'status' | 'pass_code'>): Promise<VisitorPass> => {
    const passCode = Math.floor(100000 + Math.random() * 900000).toString();
    const newPass: VisitorPass = {
      ...item,
      id: 'vis-' + Date.now(),
      pass_code: passCode,
      status: 'approved',
      created_at: new Date().toISOString(),
    };

    setVisitorPasses((prev) => [newPass, ...prev]);

    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('visitors').insert([{
          pass_code: passCode,
          host_house_number: item.host_house_number,
          host_name: item.host_name,
          visitor_name: item.visitor_name,
          license_plate: item.license_plate,
          visit_date: item.visit_date,
          purpose: item.purpose,
          status: 'approved',
        }]);
      } catch (err) {
        console.warn('Supabase create visitor pass:', err);
      }
    }

    return newPass;
  };

  const checkInVisitor = async (passCode: string): Promise<boolean> => {
    const pass = visitorPasses.find((p) => p.pass_code === passCode || p.id === passCode);
    if (!pass) return false;

    const timeStr = new Date().toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
    setVisitorPasses((prev) =>
      prev.map((p) => (p.pass_code === passCode || p.id === passCode ? { ...p, status: 'checked_in', entry_time: timeStr } : p))
    );

    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('visitors').update({ status: 'checked_in', entry_time: new Date().toISOString() }).eq('pass_code', passCode);
      } catch (err) {
        console.warn('Supabase checkin visitor:', err);
      }
    }
    return true;
  };

  const checkOutVisitor = async (passCode: string): Promise<boolean> => {
    const pass = visitorPasses.find((p) => p.pass_code === passCode || p.id === passCode);
    if (!pass) return false;

    const timeStr = new Date().toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
    setVisitorPasses((prev) =>
      prev.map((p) => (p.pass_code === passCode || p.id === passCode ? { ...p, status: 'checked_out', exit_time: timeStr } : p))
    );

    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('visitors').update({ status: 'checked_out', exit_time: new Date().toISOString() }).eq('pass_code', passCode);
      } catch (err) {
        console.warn('Supabase checkout visitor:', err);
      }
    }
    return true;
  };

  const bookFacility = async (item: Omit<FacilityBooking, 'id' | 'created_at' | 'status'>): Promise<boolean> => {
    const newBooking: FacilityBooking = {
      ...item,
      id: 'book-' + Date.now(),
      status: 'confirmed',
      created_at: new Date().toISOString(),
    };

    setBookings((prev) => [newBooking, ...prev]);

    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('facility_bookings').insert([{
          facility_id: item.facility_id,
          facility_name: item.facility_name,
          house_number: item.house_number,
          resident_name: item.resident_name,
          booking_date: item.booking_date,
          time_slot: item.time_slot,
          status: 'confirmed',
        }]);
      } catch (err) {
        console.warn('Supabase book facility:', err);
      }
    }
    return true;
  };

  const cancelBooking = async (id: string) => {
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status: 'cancelled' } : b)));
    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('facility_bookings').update({ status: 'cancelled' }).eq('id', id);
      } catch (err) {
        console.warn('Supabase cancel booking:', err);
      }
    }
  };

  // Staff Attendance Actions
  const clockIn = async (data: {
    shift_name: string;
    shift_time: string;
    photo?: string;
    location?: string;
    notes?: string;
  }): Promise<AttendanceRecord> => {
    const now = new Date();
    const timeStr = now.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) + ' น.';
    const dateStr = now.toISOString().split('T')[0];

    // Determine status (check if after scheduled start)
    const clockInStatus: ClockInStatus = 'on_time'; // Default on_time

    const newRecord: AttendanceRecord = {
      id: 'att-' + Date.now(),
      user_id: currentUser?.id || 'staff-current',
      staff_name: currentUser?.full_name || 'เจ้าหน้าที่ประจำการ',
      department: currentUser?.department || 'ฝ่ายปฏิบัติการโครงการ',
      role_title: currentUser?.role === 'staff' ? 'เจ้าหน้าที่ปฏิบัติการ' : 'ผู้บริหารจัดการนิติ',
      date: dateStr,
      shift_name: data.shift_name,
      shift_time: data.shift_time,
      clock_in_time: timeStr,
      clock_in_photo: data.photo || currentUser?.avatar_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80',
      clock_in_location: data.location || 'ป้อม รปภ. หลัก ประตู 1 (GPS Verified)',
      clock_in_status: clockInStatus,
      notes: data.notes || '',
      created_at: now.toISOString(),
    };

    setAttendanceRecords((prev) => [newRecord, ...prev]);

    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('attendance_records').insert([{
          user_id: newRecord.user_id,
          staff_name: newRecord.staff_name,
          department: newRecord.department,
          role_title: newRecord.role_title,
          date: newRecord.date,
          shift_name: newRecord.shift_name,
          shift_time: newRecord.shift_time,
          clock_in_time: newRecord.clock_in_time,
          clock_in_photo: newRecord.clock_in_photo,
          clock_in_location: newRecord.clock_in_location,
          clock_in_status: newRecord.clock_in_status,
          notes: newRecord.notes,
        }]);
      } catch (err) {
        console.warn('Supabase clockIn insert:', err);
      }
    }

    return newRecord;
  };

  const clockOut = async (id: string, data: {
    photo?: string;
    location?: string;
    notes?: string;
  }): Promise<AttendanceRecord | null> => {
    const now = new Date();
    const timeStr = now.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) + ' น.';
    let updatedRecord: AttendanceRecord | null = null;

    setAttendanceRecords((prev) =>
      prev.map((rec) => {
        if (rec.id === id) {
          const startTime = new Date(rec.created_at).getTime();
          const durationHours = Math.max(0.1, Number(((now.getTime() - startTime) / 3600000).toFixed(1)));
          updatedRecord = {
            ...rec,
            clock_out_time: timeStr,
            clock_out_photo: data.photo || rec.clock_in_photo,
            clock_out_location: data.location || 'ป้อม รปภ. หลัก ประตู 1 (GPS Verified)',
            clock_out_status: 'normal',
            work_duration_hours: durationHours,
            notes: data.notes ? (rec.notes ? `${rec.notes} | ออกงาน: ${data.notes}` : data.notes) : rec.notes,
          };
          return updatedRecord;
        }
        return rec;
      })
    );

    const client = getSupabaseClient();
    if (client && updatedRecord) {
      try {
        await client.from('attendance_records').update({
          clock_out_time: (updatedRecord as AttendanceRecord).clock_out_time,
          clock_out_photo: (updatedRecord as AttendanceRecord).clock_out_photo,
          clock_out_location: (updatedRecord as AttendanceRecord).clock_out_location,
          clock_out_status: (updatedRecord as AttendanceRecord).clock_out_status,
          work_duration_hours: (updatedRecord as AttendanceRecord).work_duration_hours,
          notes: (updatedRecord as AttendanceRecord).notes,
        }).eq('id', id);
      } catch (err) {
        console.warn('Supabase clockOut update:', err);
      }
    }

    return updatedRecord;
  };

  // Security Patrol Actions
  const recordPatrolScan = async (data: {
    checkpoint_id: string;
    round_number: number;
    round_time_slot: string;
    status: PatrolStatus;
    checklist_results: { [item: string]: boolean };
    note?: string;
    photo_url?: string;
    location_verified?: boolean;
  }): Promise<PatrolScanLog | null> => {
    const cp = patrolCheckpoints.find((c) => c.id === data.checkpoint_id);
    if (!cp) return null;

    const now = new Date();
    const timeStr = now.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) + ' น.';

    const newLog: PatrolScanLog = {
      id: 'scan-' + Date.now(),
      checkpoint_id: cp.id,
      checkpoint_code: cp.code,
      checkpoint_name: cp.name,
      checkpoint_zone: cp.zone,
      officer_id: currentUser?.id || 'officer-01',
      officer_name: currentUser?.full_name || 'รปภ. ประจำเวร',
      round_number: data.round_number,
      round_time_slot: data.round_time_slot,
      scanned_at: now.toISOString(),
      scanned_time: timeStr,
      status: data.status,
      checklist_results: data.checklist_results,
      note: data.note || '',
      photo_url: data.photo_url || cp.image_url,
      location_verified: data.location_verified !== undefined ? data.location_verified : true,
    };

    setPatrolLogs((prev) => [newLog, ...prev]);

    const client = getSupabaseClient();
    if (client) {
      try {
        await client.from('patrol_scan_logs').insert([{
          checkpoint_id: newLog.checkpoint_id,
          checkpoint_code: newLog.checkpoint_code,
          checkpoint_name: newLog.checkpoint_name,
          checkpoint_zone: newLog.checkpoint_zone,
          officer_id: newLog.officer_id,
          officer_name: newLog.officer_name,
          round_number: newLog.round_number,
          round_time_slot: newLog.round_time_slot,
          scanned_at: newLog.scanned_at,
          scanned_time: newLog.scanned_time,
          status: newLog.status,
          checklist_results: newLog.checklist_results,
          note: newLog.note,
          photo_url: newLog.photo_url,
          location_verified: newLog.location_verified,
        }]);
      } catch (err) {
        console.warn('Supabase recordPatrolScan insert:', err);
      }
    }

    return newLog;
  };

  return (
    <AppContext.Provider
      value={{
        currentUser,
        authReady,
        selectedRole,
        setSelectedRole,
        login,
        logout,
        announcements,
        parcels,
        maintenanceTickets,
        bills,
        visitorPasses,
        facilities,
        bookings,
        attendanceRecords,
        patrolCheckpoints,
        patrolLogs,
        residents,
        leaveRequests,
        leaveQuotas,
        boardDiscussions,
        financialMetrics,
        addAnnouncement,
        deleteAnnouncement,
        addParcel,
        markParcelPickedUp,
        createMaintenanceTicket,
        updateMaintenanceStatus,
        payBill,
        verifyBill,
        rejectBill,
        addResident,
        updateResident,
        submitLeaveRequest,
        reviewLeaveRequest,
        addBoardDiscussion,
        castBoardVote,
        addBoardComment,
        createVisitorPass,
        checkInVisitor,
        checkOutVisitor,
        bookFacility,
        cancelBooking,
        clockIn,
        clockOut,
        recordPatrolScan,
        isSupabaseConnected,
        isLoadingData,
        refreshData,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
